(() => {
class HistoryGallery extends HTMLElement {
  #track;
  #items = [];
  #modalItems = [];
  #modalIndex = 0;
  #raf = null;
  #position = 0;
  #resetPoint = 0;
  #paused = false;
  #motion = null;
  #mobileQuery = window.matchMedia('(max-width: 767px)');
  #dialog;
  #dialogImage;

  connectedCallback() {
    requestAnimationFrame(() => this.#initialize());
  }

  #initialize() {
    this.#track = this.querySelector('.history-gallery__track');
    if (!this.#track || this.dataset.historyGalleryReady === 'true') return;

    this.dataset.historyGalleryReady = 'true';
    this.#items = Array.from(this.querySelectorAll('.history-gallery__item'));
    this.#dialog = this.#createDialog();
    this.#dialogImage = this.#dialog.querySelector('.history-gallery__dialog-image');
    this.#bindEvents();
    this.#setMode();
  }

  disconnectedCallback() {
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#mobileQuery.removeEventListener('change', this.#setMode);
    this.#dialog?.remove();
  }

  #createDialog() {
    const dialog = document.createElement('dialog');
    const previousLabel = this.querySelector('[data-history-gallery-previous]')?.getAttribute('aria-label') || 'Previous image';
    const nextLabel = this.querySelector('[data-history-gallery-next]')?.getAttribute('aria-label') || 'Next image';
    dialog.className = 'history-gallery__dialog';
    dialog.setAttribute('aria-label', this.getAttribute('aria-label') || 'Image gallery');
    dialog.innerHTML = `
      <button class="history-gallery__dialog-close" type="button" aria-label="${this.dataset.closeLabel || 'Close dialog'}">×</button>
      <button class="history-gallery__dialog-nav history-gallery__dialog-nav--previous" type="button" aria-label="${previousLabel}"><span aria-hidden="true">&#8249;</span></button>
      <div class="history-gallery__dialog-stage" aria-live="polite">
        <img class="history-gallery__dialog-image" alt="">
      </div>
      <button class="history-gallery__dialog-nav history-gallery__dialog-nav--next" type="button" aria-label="${nextLabel}"><span aria-hidden="true">&#8250;</span></button>
    `;
    document.body.append(dialog);
    return dialog;
  }

  #bindEvents() {
    const uniqueCount = Math.ceil(this.#items.length / 2);
    this.#modalItems = this.#items.slice(0, uniqueCount).filter((item) => item.getAttribute('aria-hidden') !== 'true');

    for (const [index, item] of this.#items.entries()) {
      if (index >= uniqueCount || item.getAttribute('aria-hidden') === 'true') continue;
      item.setAttribute('role', 'button');
      item.setAttribute('tabindex', '0');
      item.setAttribute('aria-label', this.dataset.openLabel || item.querySelector('img')?.alt || 'Open image in full screen');
      item.addEventListener('click', () => this.#openDialog(item));
      item.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.#openDialog(item);
      });
    }

    this.querySelector('[data-history-gallery-previous]')?.addEventListener('click', () => this.#moveBy(-1));
    this.querySelector('[data-history-gallery-next]')?.addEventListener('click', () => this.#moveBy(1));
    this.addEventListener('mouseenter', () => { this.#paused = true; });
    this.addEventListener('mouseleave', () => { this.#paused = false; });
    this.addEventListener('focusin', () => { this.#paused = true; });
    this.addEventListener('focusout', () => { this.#paused = false; });
    this.#mobileQuery.addEventListener('change', this.#setMode);

    this.#dialog.querySelector('.history-gallery__dialog-close')?.addEventListener('click', () => this.#dialog.close());
    this.#dialog.querySelector('.history-gallery__dialog-nav--previous')?.addEventListener('click', () => this.#stepModal(-1));
    this.#dialog.querySelector('.history-gallery__dialog-nav--next')?.addEventListener('click', () => this.#stepModal(1));
    this.#dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      this.#stepModal(event.key === 'ArrowLeft' ? -1 : 1);
    });
    this.#dialog.addEventListener('click', (event) => {
      if (event.target === this.#dialog) this.#dialog.close();
    });
    this.#dialog.addEventListener('close', () => { this.#paused = false; });
  }

  #openDialog(item) {
    const itemIndex = this.#modalItems.indexOf(item);
    this.#modalIndex = itemIndex >= 0 ? itemIndex : 0;
    if (!this.#dialogImage || !this.#modalItems.length) return;

    this.#paused = true;
    this.#showModalImage();
    this.#dialog.showModal();
  }

  #stepModal(direction) {
    if (!this.#modalItems.length) return;
    this.#modalIndex = (this.#modalIndex + direction + this.#modalItems.length) % this.#modalItems.length;
    this.#showModalImage(direction);
  }

  #showModalImage(direction = 0) {
    const image = this.#modalItems[this.#modalIndex]?.querySelector('img');
    if (!image || !this.#dialogImage) return;

    this.#dialogImage.src = image.currentSrc || image.src;
    this.#dialogImage.alt = image.alt;
    if (direction === 0 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#dialogImage.animate([
      { opacity: 0.45, transform: `translate3d(${direction * 1.25}rem, 0, 0) scale(0.99)` },
      { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' }
    ], { duration: 280, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' });
  }

  #setMode = () => {
    if (this.#raf) {
      cancelAnimationFrame(this.#raf);
      this.#raf = null;
    }

    this.#position = 0;
    this.#motion = null;
    this.#track.style.transform = 'none';
    this.#paused = this.#mobileQuery.matches;
    if (!this.#mobileQuery.matches && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#startLoop();
    }
  };

  #measureResetPoint() {
    const duplicateStart = Math.floor(this.#items.length / 2);
    return this.#items[duplicateStart]?.offsetLeft || this.#track.scrollWidth / 2;
  }

  #moveBy(direction) {
    const first = this.#items[0];
    const second = this.#items[1];
    if (!first) return;

    const itemDistance = second ? second.offsetLeft - first.offsetLeft : first.getBoundingClientRect().width;
    const distance = Math.max(itemDistance, this.clientWidth * 0.78);
    if (this.#mobileQuery.matches) {
      this.#track.scrollBy({ left: distance * direction, behavior: 'smooth' });
      return;
    }

    this.#resetPoint = this.#measureResetPoint();
    if (this.#resetPoint <= 0) return;

    const travelDistance = Math.min(distance, this.#resetPoint);
    let startPosition = this.#normalizePosition(this.#position);
    let targetPosition = startPosition - (travelDistance * direction);

    if (targetPosition > 0) {
      startPosition -= this.#resetPoint;
      targetPosition -= this.#resetPoint;
    }

    this.#position = startPosition;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#position = this.#normalizePosition(targetPosition);
      this.#track.style.transform = `translate3d(${this.#position}px, 0, 0)`;
      return;
    }

    this.#motion = {
      start: startPosition,
      target: targetPosition,
      startTime: performance.now(),
      duration: 780
    };
  }

  #normalizePosition(position) {
    if (this.#resetPoint <= 0) return position;

    let normalizedPosition = position;
    while (normalizedPosition <= -this.#resetPoint) normalizedPosition += this.#resetPoint;
    while (normalizedPosition > 0) normalizedPosition -= this.#resetPoint;
    return normalizedPosition;
  }

  #easeOutQuint(progress) {
    return 1 - Math.pow(1 - progress, 5);
  }

  #startLoop() {
    const tick = () => {
      this.#resetPoint = this.#measureResetPoint();
      if (this.#motion) {
        const elapsed = performance.now() - this.#motion.startTime;
        const progress = Math.min(elapsed / this.#motion.duration, 1);
        const easedProgress = this.#easeOutQuint(progress);
        this.#position = this.#motion.start + ((this.#motion.target - this.#motion.start) * easedProgress);

        if (progress >= 1) {
          this.#position = this.#normalizePosition(this.#position);
          this.#motion = null;
        }

        this.#track.style.transform = `translate3d(${this.#position}px, 0, 0)`;
      } else if (!this.#paused && this.#resetPoint > 0) {
        this.#position -= 0.45;
        if (Math.abs(this.#position) >= this.#resetPoint) this.#position += this.#resetPoint;
        this.#track.style.transform = `translate3d(${this.#position}px, 0, 0)`;
      }
      this.#raf = requestAnimationFrame(tick);
    };
    this.#raf = requestAnimationFrame(tick);
  }
}

if (!customElements.get('history-gallery')) {
  customElements.define('history-gallery', HistoryGallery);
}

const revealHistoryCards = (root = document) => {
  const cards = root.querySelectorAll('.history-timeline__card:not([data-history-reveal-ready])');
  if (!cards.length) return;

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    }
  }, { threshold: 0.15, rootMargin: '0px 0px -3rem 0px' });

  for (const card of cards) {
    card.dataset.historyRevealReady = 'true';
    observer.observe(card);
  }
};

revealHistoryCards();
document.addEventListener('shopify:section:load', (event) => revealHistoryCards(event.target));
})();
